import React from 'react';
import { Search, Filter, ChevronDown, ChevronRight } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

const QuotationList = ({
  sortedQuotations,
  expandedQuotation,
  setExpandedQuotation,
  getStatusDetails,
  handleViewQuotation,
  setActiveView,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  sortField,
  sortDirection,
  handleSort,
  quotationStatuses
}) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">報價單列表</h2>
        <button 
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          onClick={() => setActiveView('dashboard')}
        >
          返回儀表板
        </button>
      </div>
      
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="搜索報價單..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex-shrink-0">
            <div className="relative inline-block text-left">
              <div className="flex items-center">
                <Filter className="mr-2 h-5 w-5 text-gray-400" />
                <select
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">所有狀態</option>
                  {quotationStatuses && quotationStatuses.map(status => (
                    <option key={status.status_id} value={status.status_id}>
                      {status.status_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Quotations Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('quotation_number')}
                >
                  報價單號
                  {sortField === 'quotation_number' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('customer_name')}
                >
                  客戶
                  {sortField === 'customer_name' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('issue_date')}
                >
                  日期
                  {sortField === 'issue_date' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('expiry_date')}
                >
                  有效期至
                  {sortField === 'expiry_date' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('total_amount')}
                >
                  金額
                  {sortField === 'total_amount' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('status_id')}
                >
                  狀態
                  {sortField === 'status_id' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedQuotations.map((quotation) => {
                const isExpanded = expandedQuotation === quotation.id;
                const status = getStatusDetails(quotation.status_id);
                
                return (
                  <React.Fragment key={quotation.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button 
                          onClick={() => setExpandedQuotation(isExpanded ? null : quotation.id)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5" />
                          ) : (
                            <ChevronRight className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{quotation.quotation_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{quotation.customer_name || '未知客戶'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{quotation.issue_date}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{quotation.expiry_date}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(quotation.total_amount)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span 
                          className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" 
                          style={{ backgroundColor: `${status.color_code}20`, color: status.color_code }}
                        >
                          {status.status_name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button 
                          onClick={() => handleViewQuotation(quotation)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          查看
                        </button>
                      </td>
                    </tr>
                    
                    {isExpanded && (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">客戶資料</h4>
                              <p className="text-sm text-gray-600">聯絡人: {quotation.contact_person || '未知'}</p>
                              <p className="text-sm text-gray-600">電郵: {quotation.contact_email || '未知'}</p>
                              <p className="text-sm text-gray-600">電話: {quotation.contact_phone || '未知'}</p>
                            </div>
                            
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">報價詳情</h4>
                              <p className="text-sm text-gray-600">小計: {formatCurrency(quotation.subtotal || quotation.total_amount)}</p>
                              <p className="text-sm text-gray-600">折扣: {formatCurrency(quotation.discount_amount || 0)}</p>
                              <p className="text-sm text-gray-600">稅額: {formatCurrency(quotation.tax_amount || 0)}</p>
                              <p className="text-sm font-medium text-gray-900">總額: {formatCurrency(quotation.total_amount)}</p>
                            </div>
                          </div>
                          
                          <div className="mt-4">
                            <h4 className="font-medium text-gray-900 mb-2">商品列表</h4>
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">產品</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">描述</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">數量</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">單價</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">小計</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {quotation.items && quotation.items.map((item, index) => (
                                    <tr key={index}>
                                      <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{item.product_name}</td>
                                      <td className="px-4 py-2 text-sm text-gray-500">{item.description}</td>
                                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-500">{item.quantity} {item.unit}</td>
                                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-500">{formatCurrency(item.unit_price)}</td>
                                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">{formatCurrency(item.subtotal)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuotationList;
